import React, { useState } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Wand2, Video, ShoppingBag, Swords, GraduationCap, Mic, LayoutGrid, Trophy, Briefcase,
  Sparkles, ChevronDown, Menu, X, ChevronsLeft, Cloud, Megaphone, Bot, Cog, Server, Award, Mail, BadgeCheck,
  MessagesSquare, Building2, Crown, LifeBuoy, Users, Clapperboard, BookMarked, Flame, LogOut,
  User as UserIcon, Shield, Sun, Moon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import NotificationBell from "@/components/NotificationBell";
import HelpSupportModal from "@/components/HelpSupportModal";

const NAV_SECTIONS = [
  { key: "resume-builder", to: "/resume-builder", label: "AI Resume Builder", icon: Wand2, badge: "New",
    children: [{ to: "/resume-builder", label: "Build Resume" }, { to: "/resume-templates", label: "Browse Templates" }] },
  { key: "meet", to: "/meetings", label: "Cloudivo Meet", icon: Video, badge: "New",
    children: [{ to: "/meetings", label: "Meet Dashboard" }, { to: "/meetings/all", label: "All Meetings" }, { to: "/meetings/recordings", label: "Recordings" }] },
  { key: "courses", to: "/courses", label: "Courses", icon: ShoppingBag, badge: "New",
    children: [{ to: "/courses", label: "Browse Courses" }, { to: "/my-purchases", label: "My Purchases" }, { to: "/creator/studio", label: "Creator Studio" }, { to: "/resume-builder", label: "AI Resume Builder" }] },
  { key: "duel", to: "/duel", label: "Duel", icon: Swords },
  { key: "career", to: "/career", label: "Career", icon: GraduationCap,
    children: [{ to: "/career", label: "Career Center" }, { to: "/mentorship", label: "1:1 Mentorship" }, { to: "/resume-builder", label: "AI Resume Builder" }] },
  { key: "interview", to: "/interview", label: "Interview", icon: Mic },
  { key: "resume-templates", to: "/resume-templates", label: "Resume Templates", icon: LayoutGrid },
  { key: "leaderboard", to: "/leaderboard", label: "Leaderboard", icon: Trophy },
  { key: "jobs", to: "/jobs", label: "Jobs", icon: Briefcase },
];

const AI_SKILLS_ITEM = {
  key: "ai-skills", to: "/ai-skills", label: "AI Skills", icon: Sparkles,
  children: [
    { to: "/roadmap", label: "Data Cloud", icon: Cloud, color: "text-indigo-400" },
    { to: "/sfmc", label: "SFMC", icon: Megaphone, color: "text-teal-400" },
    { to: "/mc-next", label: "MC Next", icon: Bot, color: "text-fuchsia-400" },
    { to: "/admin-dev", label: "Admin + Dev", icon: Cog, color: "text-amber-400" },
    { to: "/servicenow", label: "ServiceNow", icon: Server, color: "text-emerald-400" },
  ],
};

const ALL_GROUPS = [...NAV_SECTIONS.filter((i) => i.children), AI_SKILLS_ITEM];

function computeInitialOpen(pathname) {
  const open = {};
  ALL_GROUPS.forEach((item) => {
    if (item.children.some((c) => pathname === c.to || pathname.startsWith(`${c.to}/`))) open[item.key] = true;
  });
  return open;
}

function SidebarGroup({ item, isOpen, onToggle, collapsed, closeMobile }) {
  const location = useLocation();
  const childActive = item.children?.some((c) => location.pathname === c.to || location.pathname.startsWith(`${c.to}/`));
  const selfActive = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
  const active = selfActive || childActive;

  return (
    <div>
      <div className={`flex items-center gap-1 rounded-lg transition-colors ${active ? "bg-primary/15" : "hover:bg-white/5"}`}>
        <Link to={item.to} onClick={closeMobile} data-testid={`sidebar-nav-${item.key}`}
          className={`flex-1 flex items-center gap-3 px-3 py-2.5 text-sm min-w-0 ${active ? "text-white font-semibold" : "text-slate-400 hover:text-white"}`}>
          <item.icon className="w-4.5 h-4.5 shrink-0" />
          {!collapsed && <span className="truncate">{item.label}</span>}
          {!collapsed && item.badge && (
            <span className="text-[9px] font-bold uppercase bg-gradient-to-r from-amber-400 to-orange-500 text-white px-1.5 py-0.5 rounded-full shrink-0">{item.badge}</span>
          )}
        </Link>
        {!collapsed && item.children && (
          <button onClick={onToggle} data-testid={`sidebar-toggle-${item.key}`} className="pr-2.5 py-2.5 text-slate-500 hover:text-white shrink-0">
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
      {!collapsed && item.children && isOpen && (
        <div className="ml-4 mt-0.5 mb-1 pl-3 border-l border-white/10 space-y-0.5" data-testid={`sidebar-subnav-list-${item.key}`}>
          {item.children.map((c, i) => {
            const Ic = c.icon;
            return (
              <NavLink key={i} to={c.to} onClick={closeMobile} data-testid={`sidebar-subnav-${item.key}-${i}`}
                className={({ isActive }) => `flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs truncate transition-colors ${isActive ? "text-white font-semibold bg-white/5" : "text-slate-500 hover:text-white"}`}>
                {Ic && <Ic className={`w-3.5 h-3.5 shrink-0 ${c.color || ""}`} />}
                <span className="truncate">{c.label}</span>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ProfileMenu({ user, collapsed, onLogout, navigate }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="w-full flex items-center gap-2.5 px-1.5 py-1.5 rounded-lg hover:bg-white/5 transition-colors focus:outline-none" data-testid="sidebar-avatar-button">
          <Avatar className="h-8 w-8 border-2 border-white/10 shrink-0">
            <AvatarImage src={user.picture} alt={user.name} />
            <AvatarFallback className="bg-primary/20 text-white text-xs">{user.name?.[0]?.toUpperCase() || "U"}</AvatarFallback>
          </Avatar>
          {!collapsed && <span className="text-sm text-white truncate flex-1 text-left">{user.name}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-56">
        <DropdownMenuLabel>{user.name}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-item-profile"><UserIcon className="w-4 h-4 mr-2" /> Profile</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/friends")} data-testid="menu-item-friends"><Users className="w-4 h-4 mr-2" /> Friends</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/team")} data-testid="menu-item-team"><Building2 className="w-4 h-4 mr-2" /> Team</DropdownMenuItem>
        {!user.is_premium && (
          <DropdownMenuItem onClick={() => navigate("/premium")} data-testid="menu-item-premium"><Crown className="w-4 h-4 mr-2 text-amber-400" /> Go Premium</DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => navigate("/certificate")} data-testid="menu-item-certificate"><Award className="w-4 h-4 mr-2" /> Certificate</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/certifications")} data-testid="menu-item-certifications"><BadgeCheck className="w-4 h-4 mr-2" /> Certification Prep</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/community")} data-testid="menu-item-community"><MessagesSquare className="w-4 h-4 mr-2" /> Community</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/creator/studio")} data-testid="menu-item-creator-studio"><Clapperboard className="w-4 h-4 mr-2" /> Creator Studio</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/my-purchases")} data-testid="menu-item-my-purchases"><BookMarked className="w-4 h-4 mr-2" /> My Purchases</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/messages")} data-testid="menu-item-messages"><Mail className="w-4 h-4 mr-2" /> Recruiter Messages</DropdownMenuItem>
        {user.role === "admin" && (
          <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-item-admin"><Shield className="w-4 h-4 mr-2" /> Admin Panel</DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout} data-testid="menu-item-logout"><LogOut className="w-4 h-4 mr-2" /> Logout</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AppSidebar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [openMap, setOpenMap] = useState(() => computeInitialOpen(location.pathname));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Auto-open a group whenever navigation (from anywhere, not just a sidebar click)
  // lands on one of its child pages — merges into openMap, never auto-closes others.
  React.useEffect(() => {
    const matched = ALL_GROUPS.filter((item) => item.children.some((c) => location.pathname === c.to || location.pathname.startsWith(`${c.to}/`)));
    if (!matched.length) return;
    setOpenMap((prev) => {
      const next = { ...prev };
      let changed = false;
      matched.forEach((item) => { if (!next[item.key]) { next[item.key] = true; changed = true; } });
      return changed ? next : prev;
    });
  }, [location.pathname]);

  if (!user) return null;

  const handleLogout = async () => { await logout(); navigate("/"); };
  const closeMobile = () => setMobileOpen(false);
  const toggle = (key) => setOpenMap((prev) => ({ ...prev, [key]: !prev[key] }));

  const sidebarBody = (
    <aside className={`shrink-0 bg-[#0a0e17] border-r border-white/10 flex flex-col h-full transition-all ${collapsed ? "w-[72px]" : "w-64"}`} data-testid="app-sidebar">
      <div className="flex items-center gap-2.5 px-4 h-16 border-b border-white/10 shrink-0">
        <Link to="/dashboard" onClick={closeMobile} className="flex items-center gap-2 flex-1 min-w-0" data-testid="sidebar-logo-link">
          <img src="/icons/cloudivo-mark.png" alt="Cloudivo" className="w-8 h-8 object-contain shrink-0" />
          {!collapsed && <span className="font-heading font-semibold text-white truncate">Cloudivo</span>}
        </Link>
        <button className="md:hidden text-slate-400 hover:text-white shrink-0" onClick={closeMobile} data-testid="sidebar-mobile-close-btn">
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2.5 space-y-0.5">
        <NavLink to="/dashboard" onClick={closeMobile} data-testid="sidebar-nav-dashboard"
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-primary/15 text-white font-semibold" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
          <LayoutDashboard className="w-4.5 h-4.5 shrink-0" />
          {!collapsed && <span className="truncate">Dashboard</span>}
        </NavLink>

        <div className="pt-1 space-y-0.5">
          {NAV_SECTIONS.map((item) => (
            <SidebarGroup key={item.key} item={item} isOpen={!!openMap[item.key]} onToggle={() => toggle(item.key)} collapsed={collapsed} closeMobile={closeMobile} />
          ))}
        </div>

        <div className="pt-2 mt-2 border-t border-white/10">
          <SidebarGroup item={AI_SKILLS_ITEM} isOpen={!!openMap[AI_SKILLS_ITEM.key]} onToggle={() => toggle(AI_SKILLS_ITEM.key)} collapsed={collapsed} closeMobile={closeMobile} />
        </div>
      </nav>

      <div className="border-t border-white/10 p-2.5 space-y-2 shrink-0">
        {!collapsed && (
          <div className="flex items-center gap-1.5 px-1">
            <div className="flex-1 flex items-center gap-1.5 bg-[#ffb020]/10 border border-[#ffb020]/25 rounded-full px-2.5 py-1 justify-center" data-testid="sidebar-xp-badge">
              <Award className="w-3 h-3 text-[#ffb020]" />
              <span className="text-[11px] font-bold font-mono text-[#ffb020]">{user.xp ?? 0} XP</span>
            </div>
            <div className="flex-1 flex items-center gap-1.5 bg-[#ff8a3d]/10 border border-[#ff8a3d]/25 rounded-full px-2.5 py-1 justify-center" data-testid="sidebar-streak-badge">
              <Flame className="w-3 h-3 text-[#ff8a3d]" />
              <span className="text-[11px] font-bold font-mono text-[#ff8a3d]">{user.streak_current ?? 0}</span>
            </div>
          </div>
        )}
        <div className="flex items-center gap-1">
          <NotificationBell />
          <button onClick={() => setHelpOpen(true)} data-testid="sidebar-help-button" title="Need help?"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-indigo-300 hover:bg-white/5 transition-colors">
            <LifeBuoy className="w-4 h-4" />
          </button>
          <button onClick={toggleTheme} data-testid="sidebar-theme-toggle-btn" title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-amber-300 hover:bg-white/5 transition-colors">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button onClick={() => setCollapsed((v) => !v)} data-testid="sidebar-collapse-btn"
            className="hidden md:flex ml-auto w-8 h-8 rounded-lg items-center justify-center text-slate-500 hover:text-white hover:bg-white/5">
            <ChevronsLeft className={`w-4 h-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
          </button>
        </div>
        <ProfileMenu user={user} collapsed={collapsed} onLogout={handleLogout} navigate={navigate} />
      </div>
    </aside>
  );

  return (
    <>
      <div className="md:hidden sticky top-0 z-40 flex items-center gap-3 px-4 h-14 bg-[#0a0e17] border-b border-white/10">
        <button onClick={() => setMobileOpen(true)} data-testid="sidebar-mobile-open-btn" className="text-white">
          <Menu className="w-5.5 h-5.5" />
        </button>
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src="/icons/cloudivo-mark.png" alt="Cloudivo" className="w-7 h-7 object-contain" />
          <span className="font-heading font-semibold text-white text-sm">Cloudivo</span>
        </Link>
      </div>

      <div className="hidden md:block sticky top-0 h-screen">{sidebarBody}</div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="sidebar-mobile-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={closeMobile} data-testid="sidebar-mobile-backdrop" />
          <div className="absolute left-0 top-0 h-full">{sidebarBody}</div>
        </div>
      )}
      <HelpSupportModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
}
